import React, { useState } from "react";
import { useTestimonials, useDeleteTestimonial, useDuplicateTestimonial } from "./useTestimonials";
import DashboardLoader from "./DashboardLoader";

import EditSvg from "../../ui/EditSvg";
import DeleteSvg from "../../ui/DeleteSvg";
import DuplicateSvg from "../../ui/DuplicateSvg";
import TestimonialsModal from "./TestimonialsModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { getImageUrl } from "../../helper/imageHelper";

const TestimonialsManager = () => {
  const { testimonials, isLoading } = useTestimonials();
  const { deleteTestimonial, isDeleting } = useDeleteTestimonial();
  const { duplicateTestimonial, isDuplicating } = useDuplicateTestimonial();

  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleDelete = (id) => {
    setDeleteId(id);
  };

  const handleDuplicate = (id) => {
    duplicateTestimonial(id);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteTestimonial(deleteId, {
        onSuccess: () => setDeleteId(null)
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  if (isLoading) return <DashboardLoader />;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Testimonials</h1>
          <p className="text-sm text-gray-500">Manage client reviews and testimonials</p>
        </div>

        <button
          onClick={handleAddNew}
          className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-6 py-2.5 text-sm font-medium text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 active:translate-y-0 active:scale-95"
        >
          <span className="relative z-10 flex items-center gap-2">
            <svg className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Testimonial
          </span>
          <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {testimonials?.map((item) => (
          <div
            key={item.id}
            className="group relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-[#1a1f2e] dark:text-white"
          >
            <div className="p-4 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={getImageUrl(item.image)}
                  alt={item.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-bold text-lg leading-tight">{item.name}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{item.designation}</p>
                </div>
              </div>

              <div className="flex text-yellow-500">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className={`w-4 h-4 ${i < item.rating ? 'fill-current' : 'text-gray-300 dark:text-gray-600'}`} viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-4 italic">
                "{item.review_text}"
              </p>

              <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-700">
                <span className={`text-xs px-2 py-1 rounded-full ${item.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {item.active ? 'Active' : 'Inactive'}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDuplicate(item.id)}
                    disabled={isDuplicating}
                    className="transform rounded-full p-2 text-green-500 transition-all duration-300 hover:bg-green-50 hover:scale-110 hover:shadow-md dark:hover:bg-green-900/20"
                    title="Duplicate"
                  >
                    <DuplicateSvg className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    className="transform rounded-full p-2 text-blue-500 transition-all duration-300 hover:bg-blue-50 hover:scale-110 hover:shadow-md dark:hover:bg-blue-900/20"
                  >
                    <EditSvg className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={isDeleting}
                    className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                  >
                    <DeleteSvg className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <TestimonialsModal
          itemToEdit={editingItem}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="this testimonial"
        entityLabel="Testimonial"
      />
    </div>
  );
};

export default TestimonialsManager;
